import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input, Modal, Typography, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { importProjectPackage } from '../../api/data';
import { Project } from '../../@types/projects/types';
import { useAppContext } from '../../providers/AppContext';
import { useSelected } from '../../providers/selectedContext';

type ImportProjectModalProps = {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (project: Project) => void;
};

const ImportProjectModal = ({
  open,
  onClose,
  onSuccess
}: ImportProjectModalProps) => {
  const { configStates, updateConfigStates, updateData, data } = useAppContext();
  const { setSelectedInfo } = useSelected();
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [projectName, setProjectName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isOpen = open ?? configStates.showImportProject;

  const helperText = useMemo(() => {
    if (!selectedFile) {
      return 'Upload a JSON package exported from another project.';
    }
    return `${selectedFile.name} selected`;
  }, [selectedFile]);

  const resetState = () => {
    setProjectName('');
    setSelectedFile(null);
    setErrorMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setSelectedFile(nextFile);
    setErrorMessage(null);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setErrorMessage('Choose a project package file before importing.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      const importedProject = await importProjectPackage({
        file: selectedFile,
        project_name: projectName.trim() || undefined
      });
      message.success('Project imported successfully');
      
      if (onSuccess) {
        onSuccess(importedProject);
      } else {
        updateData({ projects: [importedProject, ...data.projects] });
        updateData({ wells: [], wellSamples: [], projectSamples: [] });
        setSelectedInfo({ project: importedProject, sample: undefined, well: undefined });
        updateConfigStates({
          fetchProjects: true,
          fetchWells: true,
          changeProject: true,
          showImportProject: false
        });
        navigate(`/capillary-pressure?project=${importedProject.uid}`);
      }
      
      resetState();
      
      if (!onSuccess && !onClose) {
        // Already handled above for global state
      } else if (onClose) {
        onClose();
      }
    } catch (error: any) {
      const nextError =
        error?.response?.data?.message || 'Unable to import the selected project package';
      setErrorMessage(nextError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetState();
    if (onClose) {
      onClose();
    } else {
      updateConfigStates({ showImportProject: false });
    }
  };

  return (
    <Modal open={isOpen} onCancel={handleCancel} footer={null} title="Import Project">
      <Form layout="vertical">
        <Form.Item
          label="Project package"
          extra="Import creates a new project owned by you."
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              border: '1px dashed rgba(255, 255, 255, 0.18)',
              borderRadius: '12px',
              padding: '1rem'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
              <Typography.Text>{helperText}</Typography.Text>
              <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
                Choose File
              </Button>
            </div>
            <input
              ref={fileInputRef}
              hidden
              accept="application/json,.json"
              type="file"
              onChange={handleFileChange}
            />
          </div>
        </Form.Item>
        <Form.Item
          label="Imported project title"
          extra="Leave blank to keep the package title with automatic conflict handling."
        >
          <Input
            aria-label="Imported project title"
            value={projectName}
            onChange={event => setProjectName(event.target.value)}
            placeholder="Optional new title"
          />
        </Form.Item>
        {errorMessage ? <Alert type="error" showIcon message={errorMessage} /> : null}
      </Form>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button type="primary" loading={submitting} onClick={handleImport}>
          Import Project
        </Button>
      </div>
    </Modal>
  );
};

export default ImportProjectModal;
